import styled from "styled-components"
import jsonpointer from "jsonpointer"
import { ICellRendererParams } from "ag-grid-community"
import { colorAttentionBlue60, fontSizeXs, spacingM, spacingS, spacingXs } from "@rhumbix/rmbx_design_system_web"
import { iRmbxColDef } from "../settings-files/types"

/** Purple override badge border color (includes 30% alpha). */
const OVERRIDE_BORDER_COLOR = "#CEC4FB"
/** Purple override badge background color. */
const OVERRIDE_BG_COLOR = "#EBE9F8"
/** Purple override badge text color. */
const OVERRIDE_TEXT_COLOR = "#785FF0"

/** Blue auto badge border color. */
const AUTO_BORDER_COLOR = "#90B5E2"
/** Blue auto badge background color. */
const AUTO_BG_COLOR = "#E7EFF9"

/**
 * Styled container that positions the currency value and badge
 * horizontally within the AG-Grid cell, right-aligned to match
 * currency column conventions.
 */
const CellContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    width: 100%;
    height: 100%;
`

/**
 * A pill-shaped badge indicating whether a rate value is auto-matched
 * ("Auto") or manually entered ("Override").
 */
const RateBadge = styled.span<{ isOverride: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 62px;
    padding: ${spacingXs} ${spacingS};
    border-radius: ${spacingM};
    border: 1px solid ${({ isOverride }) => (isOverride ? OVERRIDE_BORDER_COLOR : AUTO_BORDER_COLOR)};
    background-color: ${({ isOverride }) => (isOverride ? OVERRIDE_BG_COLOR : AUTO_BG_COLOR)};
    color: ${({ isOverride }) => (isOverride ? OVERRIDE_TEXT_COLOR : colorAttentionBlue60)};
    font-family: Roboto;
    font-weight: 700;
    font-size: ${fontSizeXs};
    letter-spacing: 0.02em;
    line-height: normal;
    white-space: nowrap;
`

/**
 * Cell renderer for ff-rate-input columns that displays the formatted currency
 * value alongside an "Auto" or "Override" badge.
 *
 * - "Override" (purple badge): Shown when the user has entered a manual rate value.
 * - "Auto" (blue badge): Shown when the cell displays the auto-matched placeholder rate.
 * - No badge or text: When neither a manual rate nor a placeholder rate exists.
 *
 * While editing, AG-Grid swaps in the cell editor, so this renderer only
 * controls the non-editing display.
 *
 * Parameters:
 * params (ICellRendererParams): Standard AG-Grid cell renderer params.
 *
 * Returns:
 * JSX.Element | null: The rendered cell content, or null for footer/empty rows.
 */
const FFRateInputCellRenderer = (params: ICellRendererParams & { colDef: iRmbxColDef }): JSX.Element | null => {
    if (params.node.footer) return null

    const usersCompanyId = params?.context?.currentUser?.company_id
    const pathToCompanyId = params?.colDef?.placeholderPath?.replace("base_rate", "rate_table/company_id")
    const rateTableCompanyId = pathToCompanyId ? jsonpointer.get(params.data, pathToCompanyId) : undefined
    const displayBadge = usersCompanyId !== undefined && rateTableCompanyId !== undefined && usersCompanyId === rateTableCompanyId
    const hasOverride = typeof params.value === "number"
    const hasFormattedValue = !!params.valueFormatted

    if (!hasOverride && !hasFormattedValue) return null

    return (
        <CellContainer>
            <span>{params.valueFormatted}</span>
            {displayBadge && <RateBadge isOverride={hasOverride}>{hasOverride ? "Override" : "Auto"}</RateBadge>}
        </CellContainer>
    )
}

export default FFRateInputCellRenderer
